import {
    FunctionComponent,
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { Rnd } from 'react-rnd';
import { Box, Typography } from '@mui/material';
import {
    VoucherTemplateElement,
    VoucherTemplateElementType,
    ELEMENT_TYPE_LABELS,
    ELEMENT_PLACEHOLDER_TEXT,
} from '@starjack/entities/voucherTemplate';
import { pixelsToRelative, relativeToPixels } from '../hooks/useEditorElements';

type EditorElementProps = {
    element: VoucherTemplateElement;
    defaultText?: string;
    isSelected: boolean;
    canvasWidth: number;
    canvasHeight: number;
    scaleFactor: number;
    onSelect: (type: VoucherTemplateElementType) => void;
    onMove: (type: VoucherTemplateElementType, x: number, y: number) => void;
    onResize: (
        type: VoucherTemplateElementType,
        w: number,
        h: number,
        x: number,
        y: number
    ) => void;
};

export const EditorElement: FunctionComponent<EditorElementProps> = ({
    element,
    defaultText,
    isSelected,
    canvasWidth,
    canvasHeight,
    scaleFactor,
    onSelect,
    onMove,
    onResize,
}) => {
    const rndRef = useRef<Rnd>(null);
    const hasRotation = element.rotation !== 0;

    const pxX = relativeToPixels(element.x, canvasWidth);
    const pxY = relativeToPixels(element.y, canvasHeight);
    const pxW = relativeToPixels(element.w, canvasWidth);
    const pxH = relativeToPixels(element.h, canvasHeight);

    const handleDragStop = useCallback(
        (_e: unknown, d: { x: number; y: number }) => {
            const x = pixelsToRelative(d.x / scaleFactor, canvasWidth);
            const y = pixelsToRelative(d.y / scaleFactor, canvasHeight);
            onMove(element.elementType, x, y);
        },
        [element.elementType, canvasWidth, canvasHeight, scaleFactor, onMove]
    );

    const handleResizeStop = useCallback(
        (
            _e: unknown,
            _dir: unknown,
            ref: HTMLElement,
            _delta: unknown,
            position: { x: number; y: number }
        ) => {
            const newW = pixelsToRelative(
                ref.offsetWidth / scaleFactor,
                canvasWidth
            );
            const newH = pixelsToRelative(
                ref.offsetHeight / scaleFactor,
                canvasHeight
            );
            const newX = pixelsToRelative(
                position.x / scaleFactor,
                canvasWidth
            );
            const newY = pixelsToRelative(
                position.y / scaleFactor,
                canvasHeight
            );
            onResize(element.elementType, newW, newH, newX, newY);
        },
        [element.elementType, canvasWidth, canvasHeight, scaleFactor, onResize]
    );

    const rgbColor = `rgb(${element.colorR}, ${element.colorG}, ${element.colorB})`;
    const label = ELEMENT_TYPE_LABELS[element.elementType];
    const placeholder =
        element.elementType === 'TEXT' && defaultText
            ? defaultText
            : ELEMENT_PLACEHOLDER_TEXT[element.elementType];

    // Horizontal alignment mirrors the backend element alignment (0=left, 2=right,
    // everything else centered), applied via textAlign so it also positions wrapped
    // lines the same way iText's ColumnText does.
    const textAlign =
        element.alignment === 0
            ? 'left'
            : element.alignment === 2
              ? 'right'
              : 'center';

    // Auto-fit: mirror the backend renderer (VoucherRenderer.addText), which shrinks
    // the font size in integer steps until the whole text fits the element box via
    // iText's ColumnText. Without this the editor clips overflowing text (overflow:
    // hidden) while the PDF renders it smaller, so preview size/wrapping/position
    // would diverge from the generated voucher. Measured against the real laid-out
    // node (scrollHeight); jsdom has no layout, so this is verified in the browser.
    const boxHeightPx = pxH * scaleFactor;
    const textRef = useRef<HTMLDivElement>(null);
    const measureCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const [fittedFontSizePx, setFittedFontSizePx] = useState(
        element.fontSize * scaleFactor
    );
    // Downward shift applied to the text so its baseline matches the PDF renderer.
    const [baselineOffsetPx, setBaselineOffsetPx] = useState(0);

    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el) return;
        let size = element.fontSize;
        el.style.fontSize = `${size * scaleFactor}px`;
        // iText's loop guards on currentFontSize > 0, so 1pt is the floor.
        while (size > 1 && el.scrollHeight > boxHeightPx + 0.5) {
            size -= 1;
            el.style.fontSize = `${size * scaleFactor}px`;
        }
        const fittedPx = size * scaleFactor;
        setFittedFontSizePx(fittedPx);

        // Match iText's vertical anchor. VoucherRenderer's ColumnText runs with
        // useAscender=false, so it places the first baseline at boxTop - leading
        // (leading = fontSize); HTML with line-height:1 anchors it higher. The
        // gap is ~(fontSize/2 - (ascent - descent)/2) and scales with font size,
        // which is why big fields (CODE, AMOUNT) visibly drift. Measure the real
        // font metrics and shift the text down by that gap so the editor tracks
        // the generated voucher.
        const canvas =
            measureCanvasRef.current ??
            (measureCanvasRef.current = document.createElement('canvas'));
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.font = `${element.italic ? 'italic ' : ''}${
                element.bold ? 700 : 400
            } ${fittedPx}px '${element.font}', sans-serif`;
            const m = ctx.measureText(placeholder || 'Xg') as TextMetrics & {
                fontBoundingBoxAscent?: number;
                fontBoundingBoxDescent?: number;
            };
            const ascent = m.fontBoundingBoxAscent || fittedPx * 0.8;
            const descent = m.fontBoundingBoxDescent || fittedPx * 0.2;
            setBaselineOffsetPx(
                Math.max(0, fittedPx / 2 - (ascent - descent) / 2)
            );
        }
    }, [
        element.fontSize,
        element.font,
        element.bold,
        element.italic,
        placeholder,
        pxW,
        boxHeightPx,
        scaleFactor,
    ]);

    return (
        <Rnd
            ref={rndRef}
            position={{ x: pxX * scaleFactor, y: pxY * scaleFactor }}
            size={{
                width: pxW * scaleFactor,
                height: pxH * scaleFactor,
            }}
            onDragStop={handleDragStop}
            onResizeStop={handleResizeStop}
            onMouseDown={() => onSelect(element.elementType)}
            bounds='parent'
            enableResizing={!hasRotation}
            scale={1}
            style={{
                border: isSelected
                    ? '2px solid #1976d2'
                    : '1px dashed rgba(0,0,0,0.3)',
                borderRadius: 2,
                cursor: 'move',
                zIndex: isSelected ? 10 : 1,
                boxSizing: 'border-box',
            }}
        >
            {/* Label badge above the element */}
            <Box
                sx={{
                    position: 'absolute',
                    top: -18,
                    left: 0,
                    backgroundColor: isSelected ? '#1976d2' : '#666',
                    color: '#fff',
                    fontSize: 10,
                    lineHeight: 1,
                    px: 0.5,
                    py: '2px',
                    borderRadius: '2px 2px 0 0',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                }}
            >
                {label}
            </Box>

            {/* Placeholder content. Text flows from the top-left of the box (matching
                iText's ColumnText, which fills from the upper edge downward) — not
                vertically centered — so the preview position matches the PDF. */}
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    transform: hasRotation
                        ? `rotate(${element.rotation}deg)`
                        : undefined,
                    transformOrigin: 'center center',
                    backgroundColor: isSelected
                        ? 'rgba(25, 118, 210, 0.08)'
                        : 'rgba(255,255,255,0.6)',
                }}
            >
                <Typography
                    ref={textRef}
                    component='div'
                    sx={{
                        width: '100%',
                        fontFamily: `'${element.font}', sans-serif`,
                        fontSize: fittedFontSizePx,
                        fontWeight: element.bold ? 700 : 400,
                        fontStyle: element.italic ? 'italic' : 'normal',
                        color: rgbColor,
                        // iText uses leading = fontSize (factor 1.0); matching it
                        // keeps the wrapping/overflow threshold identical.
                        lineHeight: 1,
                        // Push the text down to iText's baseline (see the
                        // measurement effect above).
                        transform: baselineOffsetPx
                            ? `translateY(${baselineOffsetPx}px)`
                            : undefined,
                        // All voucher fields render through addText → ColumnText,
                        // which wraps at word boundaries, so every type wraps here.
                        whiteSpace: 'normal',
                        textAlign,
                        overflow: 'hidden',
                        userSelect: 'none',
                        WebkitTextStroke:
                            element.outlineWidth && element.outlineWidth > 0
                                ? `${element.outlineWidth}px rgb(${element.outlineColorR ?? 0}, ${element.outlineColorG ?? 0}, ${element.outlineColorB ?? 0})`
                                : undefined,
                    }}
                >
                    {placeholder}
                </Typography>
            </Box>
        </Rnd>
    );
};
